import {lazy, type ComponentType, type LazyExoticComponent} from 'react'
import {createBrowserRouter, Navigate, useParams} from 'react-router-dom'

import Auth from '../routes/Auth'
import Error404 from '../routes/Error404'
import Root from '../routes/Root'
import PermissionRoute from '../routes/PermissionRoute'
import {EPermission, EResource} from '../types/permission.type'
import {
  SETTINGS_ENTRIES,
  type TSettingsPath,
} from '../screens/project/settings/sections'
import SettingsIndexRedirect from '../screens/project/settings/SettingsIndexRedirect'

// Lazy load screen components for code-splitting
const Login = lazy(() => import('../screens/auth/login'))
const PasswordForgot = lazy(() => import('../screens/auth/passwordForgot'))
const Register = lazy(() => import('../screens/auth/register'))
const ResetPassword = lazy(() => import('../screens/auth/resetPassword'))
const DashBoard = lazy(() => import('../screens/clients/dashBoard'))
const DashBoardClient = lazy(() => import('../screens/dashBoardClient'))
const ProjectList = lazy(() => import('../screens/clients/projectList'))
const NetworkDashboard = lazy(
  () => import('../screens/project/networkDashboard'),
)

// Project views
const ProjectDataWrapper = lazy(
  () => import('../screens/project/projects-data-wrapper'),
)
const WorkView = lazy(() =>
  import('../screens/project/projects-data-wrapper/views').then(m => ({
    default: m.WorkView,
  })),
)
const SynopticView = lazy(() => import('../screens/project/synopticView'))
const ActivityList = lazy(() => import('../screens/project/activityList'))
const NoticeList = lazy(() => import('../screens/project/noticeList'))
const ReportList = lazy(() => import('../screens/project/reportList'))
const ProtectionList = lazy(() => import('../screens/project/protectionList'))
const SettingsView = lazy(() =>
  import('../screens/project/projects-data-wrapper/views').then(m => ({
    default: m.SettingsView,
  })),
)
const ResourceCalendarView = lazy(
  () => import('../screens/project/resourceCalendar'),
)

// Settings sections — one lazy chunk per section, rendered in the settings
// layout's Outlet. Entries are added as each legacy management modal migrates.
const PreferencesSection = lazy(
  () => import('../screens/project/settings/sections/preferences'),
)
const ReferentialSection = lazy(
  () => import('../screens/project/settings/sections/referential'),
)
const JunctionsSection = lazy(
  () => import('../screens/project/settings/sections/junctions'),
)
const SectorsSection = lazy(
  () => import('../screens/project/settings/sections/sectors'),
)
const ProtectionsSection = lazy(
  () => import('../screens/project/settings/sections/protections'),
)
const SubSectorsSection = lazy(
  () => import('../screens/project/settings/sections/subsectors'),
)
const GeoMarkersSection = lazy(
  () => import('../screens/project/settings/sections/geomarkers'),
)
const TrainStationsSection = lazy(
  () => import('../screens/project/settings/sections/trainstations'),
)
const SwitchesSection = lazy(
  () => import('../screens/project/settings/sections/switches'),
)
const AccessSection = lazy(
  () => import('../screens/project/settings/sections/access'),
)
const UsersSection = lazy(
  () => import('../screens/project/settings/sections/users'),
)
const DictionarySection = lazy(
  () => import('../screens/project/settings/sections/dictionary'),
)
const ProtectionTypesSection = lazy(
  () => import('../screens/project/settings/sections/protection-types'),
)
const TeamsSection = lazy(
  () => import('../screens/project/settings/sections/teams'),
)
const ResourcesSection = lazy(
  () => import('../screens/project/settings/sections/resources'),
)
const ResourceRolesSection = lazy(
  () => import('../screens/project/settings/sections/resource-roles'),
)

/**
 * Screen of each settings entry, keyed by its path.
 */
const SETTINGS_SCREENS: Record<
  TSettingsPath,
  LazyExoticComponent<ComponentType>
> = {
  preferences: PreferencesSection,
  users: UsersSection,
  dictionary: DictionarySection,
  referential: ReferentialSection,
  junctions: JunctionsSection,
  sectors: SectorsSection,
  subsectors: SubSectorsSection,
  geomarkers: GeoMarkersSection,
  trainstations: TrainStationsSection,
  switches: SwitchesSection,
  access: AccessSection,
  protections: ProtectionsSection,
  'protection-types': ProtectionTypesSection,
  teams: TeamsSection,
  resources: ResourcesSection,
  'resource-roles': ResourceRolesSection,
}

// Permissions come from the entry itself, so the nav and the route never drift.
const settingsSectionRoutes = SETTINGS_ENTRIES.map(entry => {
  const Screen = SETTINGS_SCREENS[entry.path]
  return {
    path: entry.path,
    element: (
      <PermissionRoute resource={entry.resource} permission={entry.permission}>
        <Screen />
      </PermissionRoute>
    ),
  }
})

/**
 * Default redirect for an unmatched path *within* a client (e.g.
 * "/clients/:clientId/typo"). Builds an absolute URL from the route params so
 * the redirect cannot resolve relative to the captured splat segment (which
 * would otherwise append endlessly, e.g. ".../typo/projects/projects/...").
 */
function RedirectToClientDefault() {
  const {clientId} = useParams()
  return <Navigate to={`/clients/${clientId}/projects`} replace />
}

/**
 * Default redirect for an unmatched path *within* a network (e.g.
 * "/clients/:clientId/network/:networkId/workxxx"). Absolute path for the same
 * reason as RedirectToClientDefault.
 */
function RedirectToNetworkDefault() {
  const {clientId, networkId} = useParams()
  return (
    <Navigate
      to={`/clients/${clientId}/network/${networkId}/synoptic`}
      replace
    />
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <Error404 />,
    children: [
      {
        path: '/',
        element: <DashBoardClient />,
      },
      {
        path: '/clients/:clientId',
        element: <DashBoard />,
        children: [
          {
            index: true,
            element: <RedirectToClientDefault />,
          },
          {
            path: 'projects',
            element: <ProjectList />,
          },
          {
            path: '*',
            element: <RedirectToClientDefault />,
          },
        ],
      },
      {
        path: '/clients/:clientId/network/:networkId',
        element: <ProjectDataWrapper />,
        children: [
          {
            index: true,
            element: <RedirectToNetworkDefault />,
          },
          {
            path: 'dashboard',
            element: (
              <PermissionRoute
                resource={EResource.ACTIVITY}
                permission={EPermission.CAN_VIEW}>
                <NetworkDashboard />
              </PermissionRoute>
            ),
          },
          {
            path: 'work',
            element: <WorkView />,
          },
          {
            path: 'synoptic',
            element: <SynopticView />,
          },
          {
            path: 'activities',
            element: <ActivityList />,
          },
          {
            path: 'notices',
            element: (
              <PermissionRoute
                resource={EResource.NOTICE}
                permission={EPermission.CAN_VIEW}>
                <NoticeList />
              </PermissionRoute>
            ),
          },
          {
            path: 'reports',
            element: (
              <PermissionRoute
                resource={EResource.ACTIVITY}
                permission={EPermission.CAN_VIEW}>
                <ReportList />
              </PermissionRoute>
            ),
          },
          {
            path: 'protections',
            element: <ProtectionList />,
          },
          {
            path: 'resource-calendar',
            element: (
              <PermissionRoute
                resource={EResource.RESOURCE}
                permission={EPermission.CAN_VIEW}>
                <ResourceCalendarView />
              </PermissionRoute>
            ),
          },
          {
            // No gate on the screen itself: each section carries its own
            // permission, and the nav only lists what the user may open.
            path: 'settings',
            element: <SettingsView />,
            children: [
              {
                index: true,
                element: <SettingsIndexRedirect />,
              },
              ...settingsSectionRoutes,
            ],
          },
          {
            path: '*',
            element: <RedirectToNetworkDefault />,
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    element: <Auth />,
    errorElement: <Error404 />,
    children: [
      {
        path: '/auth/login',
        element: <Login />,
      },
      {
        path: '/auth/register/:token',
        element: <Register />,
      },
      {
        path: '/auth/password-forgot',
        element: <PasswordForgot />,
      },
      {
        path: '/auth/reset-password/:token',
        element: <ResetPassword />,
      },
    ],
  },
  {
    // Root-level catch-all: any unmatched URL (e.g. "/nimp", "/clients" with no
    // id, "/auth/typo") gracefully redirects to the landing instead of the raw
    // error page. URLs that already carry a client/project context are handled
    // by the nested "*" routes above and stay within that context.
    path: '*',
    element: <Navigate to='/' replace />,
  },
])
